import { useMutation } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import {
	BookOpen,
	CheckCircle2,
	ChevronRight,
	FileText,
	History,
	Printer,
	RefreshCw,
	RotateCcw,
	Shield,
	Sparkles,
	Target,
	TrendingUp,
	Users,
	Zap,
} from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { ConfidenceIndicator } from "@/components/strategy/confidence-indicator";
import { RegenerationDialog } from "@/components/strategy/regeneration-dialog";
import { StartOverDialog } from "@/components/strategy/start-over-dialog";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import {
	Sheet,
	SheetContent,
	SheetDescription,
	SheetHeader,
	SheetTitle,
} from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { useStrategy, useStrategyStatus } from "@/hooks/use-strategy";
import { trackStrategyGenerated } from "@/lib/analytics-events";
import { apiService } from "@/lib/api-service";
import { adaptStrategyReveal } from "@/lib/strategy-reveal-adapter";
import type { StrategyRevealData } from "@/types/strategy-reveal";

export const Route = createFileRoute("/_app/_/strategy/dashboard")({
	component: StrategyDashboardPage,
});

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDate(dateStr: string | null | undefined): string {
	if (!dateStr) return "-";
	return new Date(dateStr).toLocaleDateString("en-US", {
		month: "long",
		day: "numeric",
		year: "numeric",
	});
}

// ---------------------------------------------------------------------------
// Page Component
// ---------------------------------------------------------------------------

function StrategyDashboardPage() {
	const navigate = useNavigate();
	const { data: statusData, isLoading: statusLoading } = useStrategyStatus();
	const { data: strategyData, isLoading: strategyLoading } = useStrategy();

	const isReady = statusData?.status === "ready";
	const isLoading = statusLoading || strategyLoading;

	if (isLoading) {
		return (
			<div className="min-h-screen bg-muted/30 p-6">
				<div className="mx-auto max-w-5xl space-y-6">
					<Skeleton className="h-10 w-64" />
					<Skeleton className="h-48 w-full" />
					<div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
						{[1, 2, 3, 4, 5, 6].map((n) => (
							<Skeleton key={`skel-${n}`} className="h-40 w-full" />
						))}
					</div>
				</div>
			</div>
		);
	}

	if (!isReady || !strategyData) {
		return (
			<div className="flex items-center justify-center min-h-[60vh]">
				<div className="text-center space-y-4">
					<p className="text-muted-foreground">
						Your strategy is not ready yet.
					</p>
					<Button onClick={() => navigate({ to: "/strategy" })}>
						View Strategy Status
					</Button>
				</div>
			</div>
		);
	}

	const strategyReveal = strategyData?.strategy_reveal as
		| Record<string, unknown>
		| undefined;
	const data = adaptStrategyReveal(strategyReveal ?? null);

	if (!data) {
		return (
			<div className="flex items-center justify-center min-h-[60vh]">
				<div className="text-center space-y-4">
					<p className="text-muted-foreground">
						Strategy data is not available in the expected format.
					</p>
					<Button onClick={() => navigate({ to: "/strategy" })}>
						View Strategy Reveal
					</Button>
				</div>
			</div>
		);
	}

	return (
		<DashboardContent
			data={data}
			strategyData={strategyData}
		/>
	);
}

// ---------------------------------------------------------------------------
// Dashboard Content
// ---------------------------------------------------------------------------

function DashboardContent({
	data,
	strategyData,
}: {
	data: StrategyRevealData;
	strategyData: Record<string, unknown>;
}) {
	const navigate = useNavigate();
	const [regenDialogOpen, setRegenDialogOpen] = useState(false);
	const [startOverDialogOpen, setStartOverDialogOpen] = useState(false);
	const [detailSheet, setDetailSheet] = useState<string | null>(null);

	const confidenceLevel = strategyData?.overall_confidence_level as
		| "high"
		| "medium"
		| "low"
		| undefined;
	const version =
		typeof strategyData?.version === "number"
			? strategyData.version
			: undefined;
	const approvedAt = strategyData?.approved_at as string | undefined;

	const regenerateMutation = useMutation({
		mutationFn: (feedback?: string) => apiService.regenerateStrategy(feedback),
		onSuccess: () => {
			trackStrategyGenerated("regeneration");
			toast.success("Strategy regeneration has been queued.");
			setRegenDialogOpen(false);
			navigate({ to: "/strategy" });
		},
		onError: (error: Error) => {
			toast.error(error.message || "Failed to queue strategy regeneration.");
		},
	});

	const startOverMutation = useMutation({
		mutationFn: ({
			complaints,
			redoInterview,
		}: {
			complaints: string;
			redoInterview: boolean;
		}) => apiService.startOver(complaints, redoInterview),
		onSuccess: (_data, variables) => {
			toast.success("Starting over with your strategy.");
			setStartOverDialogOpen(false);
			if (variables.redoInterview) {
				navigate({ to: "/interview/session" });
			} else {
				navigate({ to: "/strategy" });
			}
		},
		onError: (error: Error) => {
			toast.error(error.message || "Failed to start over.");
		},
	});

	const currentPhase = data.phases.find((p) => p.current);

	return (
		<div className="min-h-screen bg-muted/30 p-6">
			<div className="mx-auto max-w-5xl space-y-8">
				{/* Header */}
				<div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
					<div>
						<h1 className="text-2xl font-bold tracking-tight">
							Strategy Dashboard
						</h1>
						<p className="text-sm text-muted-foreground mt-1">
							Your campaign strategy at a glance
						</p>
					</div>
					<div className="flex items-center gap-2">
						<Button variant="outline" size="sm" asChild>
							<Link to="/strategy">
								<BookOpen data-icon="inline-start" className="size-4" />
								Read Strategy
							</Link>
						</Button>
						<Button
							variant="outline"
							size="sm"
							className="print:hidden"
							onClick={() => window.print()}
						>
							<Printer data-icon="inline-start" className="size-4" />
							Print
						</Button>
					</div>
				</div>

				{/* Overview card */}
				<Card>
					<CardHeader>
						<div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
							<div className="flex items-center gap-3">
								<div className="flex size-10 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/30">
									<CheckCircle2 className="size-5 text-green-600 dark:text-green-400" />
								</div>
								<div>
									<CardTitle>Strategy Approved</CardTitle>
									<CardDescription>
										{approvedAt
											? `Approved on ${formatDate(approvedAt)}`
											: "Your strategy is ready"}
									</CardDescription>
								</div>
							</div>
							<div className="flex items-center gap-3">
								{confidenceLevel && (
									<ConfidenceIndicator level={confidenceLevel} />
								)}
								{version != null && (
									<span className="text-xs text-muted-foreground">
										v{version}
									</span>
								)}
							</div>
						</div>
					</CardHeader>
				</Card>

				{/* Mindset Shift Banner */}
				{data.mindsetShift && (
					<Card className="bg-primary/5 border-primary/20">
						<CardContent className="flex items-center gap-4 py-5">
							<Sparkles className="size-5 text-primary shrink-0" />
							<div>
								<p className="text-xs font-semibold uppercase tracking-wider text-primary/70 mb-1">
									Your Transformation
								</p>
								<p className="font-medium text-foreground">
									{data.mindsetShift}
								</p>
							</div>
						</CardContent>
					</Card>
				)}

				{/* Campaign Phases Timeline */}
				<Card>
					<CardHeader>
						<CardTitle className="text-base">Campaign Phases</CardTitle>
					</CardHeader>
					<CardContent>
						<div className="flex items-center gap-1">
							{data.phases.map((phase, i) => (
								<div
									key={phase.name}
									className={`flex-1 relative ${i > 0 ? "ml-1" : ""}`}
								>
									<div
										className={`h-2 rounded-full ${
											phase.current
												? "bg-primary"
												: i < data.phases.findIndex((p) => p.current)
													? "bg-primary/40"
													: "bg-muted-foreground/20"
										}`}
									/>
									<p
										className={`text-xs mt-2 ${
											phase.current
												? "font-semibold text-primary"
												: "text-muted-foreground"
										}`}
									>
										{phase.name}
									</p>
									{phase.current && (
										<p className="text-xs text-muted-foreground">
											{phase.goal}
										</p>
									)}
								</div>
							))}
						</div>
						{data.daysUntilElection > 0 && (
							<p className="text-xs text-muted-foreground mt-3">
								{data.daysUntilElection} days until Election Day
							</p>
						)}
					</CardContent>
				</Card>

				{/* Dashboard Cards Grid */}
				<div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
					{/* Archetype Card */}
					<DashboardCard
						title="Your Archetype"
						icon={Sparkles}
						onDetail={() => setDetailSheet("archetype")}
					>
						<p className="text-lg font-semibold">{data.candidate.archetype}</p>
						<p className="text-sm text-muted-foreground mt-1 line-clamp-2">
							{data.candidate.archetypeDescription}
						</p>
					</DashboardCard>

					{/* Winning Coalition Card */}
					<DashboardCard
						title="Winning Coalition"
						icon={Users}
						onDetail={() => setDetailSheet("coalition")}
					>
						<div className="space-y-1.5">
							{data.coalition.slice(0, 3).map((seg) => (
								<div
									key={seg.segment}
									className="flex items-center justify-between text-sm"
								>
									<span className="truncate mr-2">{seg.segment}</span>
									<span className="text-muted-foreground shrink-0">
										{seg.votes.toLocaleString()}
									</span>
								</div>
							))}
							{data.coalition.length > 3 && (
								<p className="text-xs text-muted-foreground">
									+{data.coalition.length - 3} more segments
								</p>
							)}
						</div>
					</DashboardCard>

					{/* Message Card */}
					<DashboardCard
						title="Campaign Message"
						icon={Zap}
						onDetail={() => setDetailSheet("message")}
					>
						{data.messageFramework.coreMessage ? (
							<>
								<p className="text-sm font-medium line-clamp-2">
									{data.messageFramework.coreMessage}
								</p>
								{data.messageFramework.pillars.length > 0 && (
									<div className="flex flex-wrap gap-1.5 mt-2">
										{data.messageFramework.pillars.map((p) => (
											<span
												key={p}
												className="text-xs bg-muted px-2 py-0.5 rounded-full"
											>
												{p}
											</span>
										))}
									</div>
								)}
							</>
						) : (
							<p className="text-sm text-muted-foreground italic">
								Message framework will be generated with your next strategy update
							</p>
						)}
					</DashboardCard>

					{/* Advantages Card */}
					<DashboardCard
						title="Advantages"
						icon={TrendingUp}
						onDetail={() => setDetailSheet("advantages")}
					>
						<p className="text-sm line-clamp-3">{data.candidate.strengths}</p>
					</DashboardCard>

					{/* Biggest Risk Card */}
					<DashboardCard
						title="Biggest Risk"
						icon={Shield}
						onDetail={() => setDetailSheet("risk")}
					>
						{data.biggestRisk.risk ? (
							<>
								<p className="text-sm font-medium line-clamp-2">
									{data.biggestRisk.risk}
								</p>
								<p className="text-xs text-muted-foreground mt-1 line-clamp-1">
									{data.biggestRisk.mitigation}
								</p>
							</>
						) : (
							<p className="text-sm text-muted-foreground italic">
								Risk assessment will be generated with your next strategy update
							</p>
						)}
					</DashboardCard>

					{/* Phase 1 Priority Card */}
					<DashboardCard
						title={`${currentPhase?.name ?? "Phase 1"} Priority`}
						icon={Target}
						onDetail={() => setDetailSheet("priority")}
					>
						{data.phase1Priority.priority ? (
							<>
								<p className="text-sm font-medium line-clamp-2">
									{data.phase1Priority.priority}
								</p>
								<p className="text-xs text-muted-foreground mt-1 line-clamp-1">
									{data.phase1Priority.reason}
								</p>
							</>
						) : (
							<p className="text-sm text-muted-foreground italic">
								Phase priority will be generated with your next strategy update
							</p>
						)}
					</DashboardCard>
				</div>

				{/* Daily Briefing Focus */}
				{data.dailyBriefingFocus.length > 0 && (
					<Card>
						<CardHeader>
							<CardTitle className="text-base">Daily Briefing Focus</CardTitle>
							<CardDescription>
								Key areas your daily briefings will emphasize
							</CardDescription>
						</CardHeader>
						<CardContent>
							<div className="grid gap-3 sm:grid-cols-3">
								{data.dailyBriefingFocus.map((focus, i) => (
									<div
										key={`focus-${i}`}
										className="flex items-start gap-3 rounded-lg bg-muted/50 p-3"
									>
										<div className="flex size-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
											{i + 1}
										</div>
										<p className="text-sm">{focus}</p>
									</div>
								))}
							</div>
						</CardContent>
					</Card>
				)}

				{/* Key Metrics */}
				{data.keyMetrics.length > 0 && (
					<Card>
						<CardHeader>
							<CardTitle className="text-base">Key Metrics</CardTitle>
						</CardHeader>
						<CardContent>
							<div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
								{data.keyMetrics.map((m) => (
									<div key={m.metric} className="space-y-1">
										<p className="text-xs text-muted-foreground uppercase tracking-wider">
											{m.metric}
										</p>
										<div className="flex items-baseline gap-2">
											<span className="text-lg font-semibold">
												{m.current || "-"}
											</span>
											{m.target && (
												<span className="text-xs text-muted-foreground">
													/ {m.target} target
												</span>
											)}
										</div>
									</div>
								))}
							</div>
						</CardContent>
					</Card>
				)}

				<Separator />

				{/* Bottom CTAs */}
				<div className="flex flex-col gap-3 sm:flex-row sm:justify-center print:hidden">
					<Button asChild>
						<Link to="/strategy">
							<BookOpen data-icon="inline-start" className="size-4" />
							Read Strategy
						</Link>
					</Button>
					<Button variant="outline" asChild>
						<Link to="/strategy/brief">
							<FileText data-icon="inline-start" className="size-4" />
							View Full Brief
						</Link>
					</Button>
					<Button variant="outline" asChild>
						<Link to="/strategy/versions">
							<History data-icon="inline-start" className="size-4" />
							Version History
						</Link>
					</Button>
				</div>

				<Separator className="print:hidden" />

				{/* Regenerate / Start Over */}
				<div className="flex flex-col gap-3 sm:flex-row sm:justify-center print:hidden">
					<Button variant="outline" onClick={() => setRegenDialogOpen(true)}>
						<RefreshCw data-icon="inline-start" className="size-4" />
						Regenerate Strategy
					</Button>
					<Button
						variant="ghost"
						className="text-destructive hover:text-destructive"
						onClick={() => setStartOverDialogOpen(true)}
					>
						<RotateCcw data-icon="inline-start" className="size-4" />
						Start Over
					</Button>
				</div>
			</div>

			{/* Regeneration dialog */}
			<RegenerationDialog
				open={regenDialogOpen}
				onOpenChange={setRegenDialogOpen}
				type="full"
				onConfirm={(feedback) => regenerateMutation.mutate(feedback)}
				isSubmitting={regenerateMutation.isPending}
			/>

			{/* Start Over dialog */}
			<StartOverDialog
				open={startOverDialogOpen}
				onOpenChange={setStartOverDialogOpen}
				onConfirm={(complaints, redoInterview) =>
					startOverMutation.mutate({ complaints, redoInterview })
				}
				isSubmitting={startOverMutation.isPending}
			/>

			{/* Detail Sheets */}
			<DetailSheet
				open={detailSheet}
				onClose={() => setDetailSheet(null)}
				data={data}
			/>
		</div>
	);
}

// ---------------------------------------------------------------------------
// Dashboard Card
// ---------------------------------------------------------------------------

function DashboardCard({
	title,
	icon: Icon,
	children,
	onDetail,
}: {
	title: string;
	icon: React.ComponentType<{ className?: string }>;
	children: React.ReactNode;
	onDetail: () => void;
}) {
	return (
		<Card
			className="cursor-pointer transition-colors hover:bg-muted/50"
			onClick={onDetail}
		>
			<CardHeader className="pb-2">
				<div className="flex items-center justify-between">
					<div className="flex items-center gap-2">
						<Icon className="size-4 text-muted-foreground" />
						<CardTitle className="text-sm font-medium">{title}</CardTitle>
					</div>
					<ChevronRight className="size-4 text-muted-foreground" />
				</div>
			</CardHeader>
			<CardContent>{children}</CardContent>
		</Card>
	);
}

// ---------------------------------------------------------------------------
// Detail Sheet
// ---------------------------------------------------------------------------

function DetailSheet({
	open,
	onClose,
	data,
}: {
	open: string | null;
	onClose: () => void;
	data: StrategyRevealData;
}) {
	const content = getDetailContent(open, data);

	return (
		<Sheet open={!!open} onOpenChange={(isOpen) => !isOpen && onClose()}>
			<SheetContent className="overflow-y-auto">
				<SheetHeader>
					<SheetTitle>{content.title}</SheetTitle>
					<SheetDescription>{content.description}</SheetDescription>
				</SheetHeader>
				<div className="mt-6 space-y-4">{content.body}</div>
			</SheetContent>
		</Sheet>
	);
}

function getDetailContent(
	key: string | null,
	data: StrategyRevealData,
): {
	title: string;
	description: string;
	body: React.ReactNode;
} {
	switch (key) {
		case "archetype":
			return {
				title: data.candidate.archetype,
				description: "Your candidate archetype",
				body: (
					<>
						<p className="text-sm leading-relaxed">
							{data.candidate.archetypeDescription}
						</p>
						<Separator />
						<div>
							<p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
								Strengths
							</p>
							<p className="text-sm">{data.candidate.strengths}</p>
						</div>
						<div>
							<p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
								Challenges
							</p>
							<p className="text-sm">{data.candidate.challenges}</p>
						</div>
					</>
				),
			};
		case "coalition":
			return {
				title: "Winning Coalition",
				description: `${data.coalition.reduce((sum, s) => sum + s.votes, 0).toLocaleString()} total coalition votes`,
				body: (
					<div className="space-y-4">
						{data.coalition.map((seg) => (
							<div key={seg.segment} className="space-y-1">
								<div className="flex items-center justify-between">
									<p className="font-medium text-sm">{seg.segment}</p>
									<span className="text-sm text-muted-foreground">
										{seg.votes.toLocaleString()} votes
									</span>
								</div>
								<p className="text-xs text-muted-foreground">
									{seg.description}
								</p>
							</div>
						))}
					</div>
				),
			};
		case "message":
			return {
				title: "Campaign Message",
				description: "Your core message and supporting pillars",
				body: (
					<>
						{data.messageFramework.coreMessage && (
							<div className="rounded-lg bg-primary/5 border border-primary/20 p-4">
								<p className="text-sm font-medium">
									{data.messageFramework.coreMessage}
								</p>
							</div>
						)}
						{data.messageFramework.pillars.length > 0 && (
							<div>
								<p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
									Supporting Pillars
								</p>
								<div className="space-y-2">
									{data.messageFramework.pillars.map((p, i) => (
										<div
											key={p}
											className="flex items-start gap-3 rounded-lg bg-muted/50 p-3"
										>
											<div className="flex size-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
												{i + 1}
											</div>
											<p className="text-sm">{p}</p>
										</div>
									))}
								</div>
							</div>
						)}
					</>
				),
			};
		case "advantages":
			return {
				title: "Advantages",
				description: "What works in your favor",
				body: (
					<>
						<p className="text-sm leading-relaxed">
							{data.candidate.strengths}
						</p>
						<Separator />
						<div>
							<p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
								What to Watch
							</p>
							<p className="text-sm leading-relaxed">
								{data.candidate.challenges}
							</p>
						</div>
					</>
				),
			};
		case "risk":
			return {
				title: "Biggest Risk",
				description: "Your top risk and how to mitigate it",
				body: (
					<>
						<div className="rounded-lg bg-destructive/5 border border-destructive/20 p-4">
							<p className="text-sm font-medium">{data.biggestRisk.risk}</p>
						</div>
						{data.biggestRisk.mitigation && (
							<div>
								<p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
									Mitigation Strategy
								</p>
								<p className="text-sm leading-relaxed">
									{data.biggestRisk.mitigation}
								</p>
							</div>
						)}
					</>
				),
			};
		case "priority":
			return {
				title: "Current Priority",
				description: "Your #1 focus right now",
				body: (
					<>
						<div className="rounded-lg bg-primary/5 border border-primary/20 p-4">
							<p className="text-sm font-medium">
								{data.phase1Priority.priority}
							</p>
						</div>
						{data.phase1Priority.reason && (
							<div>
								<p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
									Why This Matters
								</p>
								<p className="text-sm leading-relaxed">
									{data.phase1Priority.reason}
								</p>
							</div>
						)}
					</>
				),
			};
		default:
			return { title: "", description: "", body: null };
	}
}
